import React, { useState, useEffect, useCallback } from 'react'
import { Container, Form, Spinner, Row, Col } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts'
import { amazonAPI } from '../api/amazonAPI'
import '../styles/selectControl.css'

function AmazonTACoSTrendScreen() {
    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState('')
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [selectedAsin, setSelectedAsin] = useState(null)
    const [selectedReports, setSelectedReports] = useState([])
    const [targetTacos, setTargetTacos] = useState('')

    const [trendData, setTrendData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [isDarkMode, setIsDarkMode] = useState(false)

    useEffect(() => {
        const checkDarkMode = () => {
            const theme = document.documentElement.getAttribute('data-bs-theme')
            setIsDarkMode(theme === 'dark')
        }
        checkDarkMode()
        const observer = new MutationObserver(checkDarkMode)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] })
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const loadAccounts = async () => {
            const data = await amazonAPI.accounts.getAll()
            if (data) {
                const active = data.filter(a => a.is_active)
                setAccounts(active)
                if (active.length === 1) {
                    setSelectedAccountId(String(active[0].id))
                }
            }
        }
        loadAccounts()
    }, [])

    const fetchTrend = useCallback(async () => {
        setLoading(true)
        const params = {}
        if (selectedAccountId) params.account_id = selectedAccountId
        if (startDate) params.start_date = startDate.toISOString().split('T')[0]
        if (endDate) params.end_date = endDate.toISOString().split('T')[0]
        if (selectedAsin) params.asin = selectedAsin.value
        if (selectedReports.length > 0) {
            params.report_ids = selectedReports.map(r => r.value).join(',')
        }
        const data = await amazonAPI.asinReports.getTacosTrend(params)
        setTrendData(data)
        setLoading(false)
    }, [selectedAccountId, startDate, endDate, selectedAsin, selectedReports])

    useEffect(() => {
        fetchTrend()
    }, [fetchTrend])

    const accountOptions = accounts.map(a => ({ value: String(a.id), label: a.account_name }))

    const asinOptions = (trendData?.available_asins || []).map(a => ({
        value: a.asin,
        label: `${a.asin} — ${a.product_name || ''}`,
    }))

    const reportOptions = (trendData?.available_reports || []).map(r => ({
        value: r.id,
        label: r.label,
    }))

    const chartData = (trendData?.data_points || []).map(dp => ({
        ...dp,
        tacos: dp.tacos,
        acos: dp.acos,
    }))

    const warnings = trendData?.warnings || []

    const targetValue = targetTacos !== '' ? parseFloat(targetTacos) : null

    const axisColor = isDarkMode ? '#aaa' : '#666'
    const gridColor = isDarkMode ? '#444' : '#e0e0e0'

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null
        const dp = payload[0]?.payload
        if (!dp) return null
        return (
            <div style={{
                background: isDarkMode ? '#2d2d2d' : '#fff',
                border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`,
                borderRadius: 6,
                padding: '10px 14px',
                color: isDarkMode ? '#e0e0e0' : '#333',
                fontSize: 13,
            }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{dp.label}</div>
                {dp.tacos != null && (
                    <div style={{ color: '#0d6efd' }}>TACoS: {dp.tacos}%</div>
                )}
                {dp.acos != null && (
                    <div style={{ color: '#fd7e14' }}>ACoS: {dp.acos}%</div>
                )}
                <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                    Revenue: ${dp.total_revenue?.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                    Ad Spend: ${dp.total_ad_spend?.toLocaleString()}
                </div>
            </div>
        )
    }

    return (
        <Container fluid className="px-4 py-4">
            <h1 className="mb-3">TACoS Trend</h1>

            <Row className="mb-4 g-3" style={{ maxWidth: '1100px' }}>
                <Col md={3}>
                    <Form.Label>Amazon Account</Form.Label>
                    <Select
                        value={accountOptions.find(opt => opt.value === selectedAccountId) || null}
                        onChange={(opt) => setSelectedAccountId(opt?.value || '')}
                        options={accountOptions}
                        isClearable
                        placeholder="All Accounts..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </Col>
                <Col md={2}>
                    <Form.Label>Start Date</Form.Label>
                    <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        className="form-control"
                        dateFormat="MM/dd/yyyy"
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={endDate}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        portalId="root"
                        isClearable
                    />
                </Col>
                <Col md={2}>
                    <Form.Label>End Date</Form.Label>
                    <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        className="form-control"
                        dateFormat="MM/dd/yyyy"
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        maxDate={new Date()}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        portalId="root"
                        isClearable
                    />
                </Col>
                <Col md={5}>
                    <Form.Label>ASIN</Form.Label>
                    <Select
                        value={selectedAsin}
                        onChange={(opt) => setSelectedAsin(opt)}
                        options={asinOptions}
                        isClearable
                        placeholder="All ASINs (type to search)..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        noOptionsMessage={() => trendData ? 'No ASINs found' : 'Loading...'}
                    />
                </Col>
            </Row>

            <Row className="mb-4 g-3" style={{ maxWidth: '1100px' }}>
                <Col md={6}>
                    <Form.Label>Reports</Form.Label>
                    <Select
                        value={selectedReports}
                        onChange={(opts) => setSelectedReports(opts || [])}
                        options={reportOptions}
                        isMulti
                        isClearable
                        placeholder="All Reports..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        noOptionsMessage={() => trendData ? 'No reports found' : 'Loading...'}
                    />
                </Col>
                <Col md={2}>
                    <Form.Label>Target TACoS %</Form.Label>
                    <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={targetTacos}
                        onChange={(e) => setTargetTacos(e.target.value)}
                        placeholder="e.g. 15"
                    />
                </Col>
            </Row>

            {warnings.length > 0 && (
                <div className="alert alert-warning mb-4" style={{ maxWidth: '1100px' }}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Data Quality:</strong>
                    <ul className="mb-0 mt-1">
                        {warnings.map((w, i) => (
                            <li key={i}>{w.message}</li>
                        ))}
                    </ul>
                </div>
            )}

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : chartData.length === 0 ? (
                <p className="text-muted">No completed reports found for the selected filters.</p>
            ) : (
                <div style={{ maxWidth: '1100px' }}>
                    <ResponsiveContainer width="100%" height={450}>
                        <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                            <defs>
                                <linearGradient id="tacosGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#0d6efd" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                            <XAxis
                                dataKey="label"
                                tick={{ fill: axisColor, fontSize: 12 }}
                                stroke={gridColor}
                                angle={-30}
                                textAnchor="end"
                                height={60}
                                interval={0}
                            />
                            <YAxis
                                tick={{ fill: axisColor, fontSize: 12 }}
                                stroke={gridColor}
                                tickFormatter={(v) => `${v}%`}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            {targetValue != null && (
                                <ReferenceLine
                                    y={targetValue}
                                    stroke="#dc3545"
                                    strokeDasharray="6 4"
                                    strokeWidth={1.5}
                                    label={{
                                        value: `Target ${targetValue}%`,
                                        fill: '#dc3545',
                                        fontSize: 12,
                                        position: 'right',
                                    }}
                                />
                            )}
                            <Area
                                type="monotone"
                                dataKey="tacos"
                                name="TACoS"
                                stroke="#0d6efd"
                                fill="url(#tacosGradient)"
                                strokeWidth={2}
                                dot={{ r: 4, fill: '#0d6efd' }}
                                activeDot={{ r: 6 }}
                                connectNulls
                            />
                            <Area
                                type="monotone"
                                dataKey="acos"
                                name="ACoS"
                                stroke="#fd7e14"
                                fill="none"
                                strokeWidth={2}
                                strokeDasharray="6 3"
                                dot={{ r: 4, fill: '#fd7e14' }}
                                activeDot={{ r: 6 }}
                                connectNulls
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </Container>
    )
}

export default AmazonTACoSTrendScreen
